// submitCompleteOrder.js
'use strict';
import parseLinkHeader from 'parse-link-header';

import { SHOP_PURCHASE_REDIRECT_URL } from '../RemoteConfigKeys.js';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import isEmailFormatCorrect from '../resource/isEmailFormatCorrect.js';
import getApplePayPrimeWithTappay from '../resource/getApplePayPrimeWithTappay.js';
import i18n, { localeConvertor } from '../resource/i18n.js';
import { getIsTappayApplePay } from '../resource/shopUtils.js';
import convertUrlToSafariScheme from '../resource/convertUrlToSafariScheme.js';
import getMeData from '../selector/getMeData.js';
import getOrderData from '../selector/getOrderData.js';
import pushToastr from '../action/pushToastr.js';
import addModal from '../action/addModal.js';
import removeModals from '../action/removeModals.js';
import toggleSubscribeAndUnlockModal from '../action/toggleSubscribeAndUnlockModal.js';
import subscribePresenceOrder from '../action/subscribePresenceOrder.js';
import handleOrder from '../action/handleOrder.js';
import { orderStatusConstant, SHOP_ID } from '../resource/shopConstants.js';
import { getSentry } from '../resource/sentry.js';
import {
  MERGE_ORDER_DATA,
  MERGE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  PERSIST_NOW,
} from '../ActionTypes.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getModalData from '../selector/getModalData.js';
import getProductData from '../selector/getProductData.js';
import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getFpIdPro from '../action/getFpIdPro.js';
import { FEATURE_FINGERPRINT_PRO_SHOP_COMPLETE } from '../RemoteConfigKeys.js';
import { payment as paymentDebug } from '../resource/debug.js';

const log = paymentDebug.extend('log:submitCompleteOrder');
const errorLog = paymentDebug.extend('error:submitCompleteOrder');

// parse-link-header has a built-in max length limit (default 2000 chars). Some redirect URLs (e.g. Alipay)
// can easily exceed this limit and cause parseLinkHeader(...) to return null.
//
// Note: you *can* adjust parse-link-header behavior via environment variables:
// @see: https://github.com/thlorenz/parse-link-header?tab=readme-ov-file#environmental-variables
// - PARSE_LINK_HEADER_MAXLEN: increase the max length threshold (defaults to 2000)
// - PARSE_LINK_HEADER_THROW_ON_MAXLEN_EXCEEDED: when set, it throws instead of returning null on maxlen exceeded
//
// We still keep this fallback to avoid relying on runtime env config and to make redirect parsing resilient
// across different deployment environments.
// This fallback extracts the first URL with rel="redirect" without enforcing a max length.
const getRedirectUrlFromLinkHeader = linkHeader => {
  if (!linkHeader) {
    return null;
  }

  const parsed = parseLinkHeader(linkHeader);
  if (parsed?.redirect?.url) {
    return parsed.redirect.url;
  }

  // Fallback: find the link-value whose parameters contain rel="redirect"
  // RFC5988 style: <url>; rel="redirect"[; other-params]
  const match = linkHeader.match(
    /<([^>]*)>\s*;\s*[^,]*\brel\s*=\s*"?redirect"?/i
  );
  return match?.[1] || null;
};

/**
 * Submit complete order
 * @kind action
 * @param {string} {shopId} - shop id, ex: 'addvalue', 'messagePack'.
 * @param {string} {orderId} - payment order id.
 * @param {object} {source} - payment source object. Will use sourceJointId to map original source if omitted.
 * @param {string} {sourceJointId} - payment source joint id.
 * @param {string} {subscribeUserId} - subscribe user id.
 * @return {Promise} Action promise.
 */
const submitCompleteOrder =
  ({
    shopId,
    orderId,
    source,
    sourceJointId,
    withEmail = true,
    subscribeUserId,
  }) =>
  async (dispatch, getState) => {
    const isSubscriptionShop = SHOP_ID.subscription === shopId;
    const state = getState();
    const token = getMeData(state, 'token');
    if (!source) {
      source = getOrderData(state, orderId, 'sources')?.filter(
        s => s.jointId === sourceJointId
      )?.[0];
    }
    const logContext = { shopId, orderId, sourceJointId };

    if (!token || !shopId || !orderId || !source) {
      errorLog('missing required params', {
        ...logContext,
        hasToken: !!token,
        source,
      });
      return dispatch({ type: '' });
    }

    log('start', { ...logContext, via: source.via, sourceType: source.type });

    const email =
      getOrderData(state, orderId, 'email') ||
      getMeData(state, 'orderEmail') ||
      getMeData(state, 'email');
    if (!email || !isEmailFormatCorrect({ email })) {
      return dispatch(
        addModal({
          id: `${shopId}.ConfirmOrderEmailInput`,
          isHigherThanAll: true,
        })
      );
    }
    dispatch({
      type: MERGE_ORDER_DATA,
      payload: {
        order: { id: orderId, email, shopId, subscribeUserId },
      },
    });
    dispatch(
      removeModals({
        ids: [`${shopId}.ConfirmOrderEmailInput`],
        transitionStatus: 'closing',
      })
    );

    if (getIsTappayApplePay({ source })) {
      log('tappay apple pay get prime', logContext);
      const getPrimeResult = await getApplePayPrimeWithTappay();

      if ('success' !== getPrimeResult.status) {
        errorLog('tappay apple pay get prime failed', logContext);
        return dispatch(
          pushToastr({
            textKey: getPrimeResult.payload.message,
            color: 'error',
          })
        );
      }
      // eslint-disable-next-line require-atomic-updates
      source.id = getPrimeResult.payload.prime;
      if ('tappay' === source.via) {
        source.id = `card-prime:${source.id}`;
      }
    }
    if ('card-prime:card:tappay' === sourceJointId) {
      log('open tappay card form', logContext);
      return dispatch(
        addModal({
          id: `${shopId}.TappayCardFormModal`,
          isHigherThanAll: true,
        })
      );
    }
    if ('card-prime:card:securionpay' === sourceJointId) {
      log('open securionpay card form', logContext);
      return dispatch(
        addModal({
          id: `${shopId}.SecurionpayCardFormModal`,
          isHigherThanAll: true,
        })
      );
    }

    if ('credit-card:card:centrobill' === sourceJointId) {
      log('open centrobill card form', logContext);
      return dispatch(
        addModal({
          id: `${shopId}.CentrobillCardFormModal`,
          isHigherThanAll: true,
        })
      );
    }

    if ('CreditCard:card:nineoneapp' === sourceJointId) {
      log('open nineoneapp card form', logContext);
      return dispatch(
        addModal({
          id: `${shopId}.NineoneAppCardFormModal`,
          isHigherThanAll: true,
        })
      );
    }

    const selectPath = ['shop', shopId, 'completeOrder', sourceJointId];

    const redirectUrl = new URL(location.pathname, location.origin);
    redirectUrl.searchParams.append('shopId', shopId);
    redirectUrl.searchParams.append('email', email);
    redirectUrl.searchParams.append('sourceId', source.id);
    if (subscribeUserId) {
      redirectUrl.searchParams.append('subscribeUserId', subscribeUserId);
    }
    const utm = getMeData(getState(), 'utm');
    if (utm) {
      Object.keys(utm)
        .filter(key => utm[key])
        .forEach(key => redirectUrl.searchParams.append(key, utm[key]));
    }

    const url = getResourceUrl({
      endpoint: `/shop/checkout/${source.via}/complete`,
    });
    const lang = localeConvertor({
      locale: i18n.language,
      isISO639: true,
    });
    url.searchParams.set('lang', lang);

    // modify redirect url with remote config
    const getRedirectUrl = () => {
      const shopPurchaseRedirectUrlSource = getRemoteConfigData(
        getState(),
        SHOP_PURCHASE_REDIRECT_URL
      );

      if (!shopPurchaseRedirectUrlSource) {
        return redirectUrl.href;
      }

      const shopPurchaseRedirectUrl = new URL(shopPurchaseRedirectUrlSource);
      shopPurchaseRedirectUrl.searchParams.set('email', email);
      shopPurchaseRedirectUrl.searchParams.set('order', orderId);
      shopPurchaseRedirectUrl.searchParams.set('shopId', shopId);
      shopPurchaseRedirectUrl.searchParams.set('sourceId', source.id);
      if (subscribeUserId) {
        shopPurchaseRedirectUrl.searchParams.set(
          'subscribeUserId',
          subscribeUserId
        );
      }
      shopPurchaseRedirectUrl.searchParams.set('lang', lang);
      return shopPurchaseRedirectUrl.href;
    };

    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        order: orderId,
        source: source.id,
        redirect_url: getRedirectUrl(),
        email: withEmail ? email : undefined,
      }),
    };

    const isFpProEnabled =
      getRemoteConfigData(getState(), FEATURE_FINGERPRINT_PRO_SHOP_COMPLETE) ===
      1;
    if (isFpProEnabled) {
      try {
        await Promise.race([
          dispatch(getFpIdPro({ via: 'payment.succeeded' })),
          new Promise((_, reject) => setTimeout(reject, 5000)),
        ]);
        const fpIdPro = getOperationData(
          getState(),
          ['fingerprint'],
          'fpIdPro'
        );
        if (fpIdPro) {
          fetchOptions.headers['X-Fingerprint-Pro-Id'] = fpIdPro;
        }
      } catch {
        // silent fail - don't block order completion
      }
    }

    dispatch({ type: PERSIST_NOW });
    dispatch({ type: SET_NETWORKING_FETCHING, payload: { selectPath } });

    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      // If the user is creator, they will encounter a 401 {"code":"MISSING_SCOPES"} error.
      // After the 401 refresh token flow, user will still encounter the same 401 {"code":"MISSING_SCOPES"} error.
      // The result from `resource/handleFetchError.js` will return same 401 response instead of throw an error.
      // We should break here instead of continue subsequent process.
      if (response.status === 401) {
        const text = await response.text();
        throw new Error(text);
      }

      log('response received', { ...logContext, status: response.status });

      if (200 === response.status) {
        // clean shop operation data to prevent duplicate transactions
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['shop', shopId],
            data: {
              submitOrderId: undefined,
              selectPaymentSourceJointId: undefined,
              selectedDiamondPackId: undefined,
            },
          },
        });

        const items = getOrderData(state, orderId, 'items') || [];
        const payload = {
          orderId,
          amount: getOrderData(state, orderId, 'amount'),
          status: orderStatusConstant.SUCCEEDED,
          currency: getOrderData(state, orderId, 'currency'),
          email: getOrderData(state, orderId, 'email'),
          items,
        };
        const { withScope, captureMessage } = await getSentry();
        if (withScope && captureMessage) {
          withScope(scope => {
            scope.setContext('payload', {
              ...payload,
              items: items.map(item => {
                const productId = item.productId;
                return {
                  ...item,
                  productType: getProductData(getState(), [productId], 'type'),
                };
              }),
            });
            scope.setFingerprint(['order', 'completed']);
            captureMessage('Complete order receive 200', 'log');
          });
        }
        dispatch(handleOrder(payload));

        const isOrderSummaryOpened =
          getModalData(getState(), `${shopId}.OrderSummary`, 'zIndex') !== null;
        if (isOrderSummaryOpened) {
          dispatch(
            removeModals({
              ids: [`${shopId}.OrderSummary`],
              transitionStatus: 'closing',
            })
          );
        }

        dispatch(
          removeModals({
            ids: [`${shopId}.NineoneAppApplePay`, `${shopId}.EcpayApplePay`],
          })
        );

        return dispatch({
          type: SET_NETWORKING_SUCCESS,
          payload: { selectPath },
        });
      } else if (201 === response.status) {
        // clean shop operation data to prevent duplicate transactions
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['shop', shopId],
            data: {
              submitOrderId: undefined,
              selectPaymentSourceJointId: undefined,
              selectedDiamondPackId: undefined,
            },
          },
        });

        dispatch({
          type: MERGE_ORDER_DATA,
          payload: {
            order: {
              id: orderId,
              isInAppPurchase: true,
            },
          },
        });
        dispatch(
          subscribePresenceOrder({
            orderId,
          })
        );
        return dispatch({
          type: SET_NETWORKING_SUCCESS,
          payload: { selectPath },
        });
      } else if (202 === response.status) {
        const linkHeader = response.headers.get('Link');
        const parsedLinkHeader = parseLinkHeader(linkHeader);
        const { redirect } = parsedLinkHeader || {};
        const redirectUrlString =
          redirect?.url || getRedirectUrlFromLinkHeader(linkHeader);
        if (!redirectUrlString) {
          throw new Error('no_redirect_link_found');
        }
        dispatch({ type: SET_NETWORKING_SUCCESS, payload: { selectPath } });
        const redirectUrl = new URL(redirectUrlString);
        if ('/shop/tappay/add-payment-source.html' === redirectUrl.pathname) {
          return dispatch(
            addModal({
              id: `${shopId}.TappayCardFormModal`,
              isHigherThanAll: true,
            })
          );
        }
        if (
          '/shop/securionpay/add-payment-source.html' === redirectUrl.pathname
        ) {
          return dispatch(
            addModal({
              id: `${shopId}.SecurionpayCardFormModal`,
              isHigherThanAll: true,
            })
          );
        }
        if (
          redirectUrl.pathname.startsWith('/shop/ecpay/add-payment-source.html')
        ) {
          dispatch({
            type: MERGE_ORDER_DATA,
            payload: {
              order: {
                id: orderId,
                ecpayToken: redirect?.['merch-token'],
              },
            },
          });
          return dispatch(
            addModal({
              id: `${shopId}.EcpayCardFormModal`,
              isHigherThanAll: true,
            })
          );
        }
        if (
          redirectUrl.pathname.startsWith(
            '/shop/nineoneapp/add-payment-source.html'
          )
        ) {
          return dispatch(
            addModal({
              id: `${shopId}.NineoneAppCardFormModal`,
              isHigherThanAll: true,
            })
          );
        }
        dispatch(
          removeModals({
            ids: [
              `${shopId}.OrderSummary`,
              `${shopId}.OrderProcessingInAppAlert`,
              `${shopId}.TappayCardFormModal`,
              `${shopId}.SecurionpayCardFormModal`,
              `${shopId}.EcpayCardFormModal`,
              `${shopId}.EcpayApplePay`,
              `${shopId}.NineoneAppCardFormModal`,
            ],
            transitionStatus: 'closing',
          })
        );
        if (
          isSubscriptionShop &&
          getModalData(getState(), 'SubscribeAndUnlockModal', 'status') ===
            'display'
        ) {
          dispatch(toggleSubscribeAndUnlockModal({ isOpen: false }));
        }

        const sourceType = source.type;
        // Due to unstable payment methods in China, there are often multiple routes for users to try
        // Use a blacklist to record which routes users have already tried, to prevent them from repeatedly trying unusable routes
        const needRetryCooldown = source?.context?.need_retry_cooldown;
        if (needRetryCooldown) {
          const timestampOffsetSeconds = getTimestampOffset(
            getState(),
            Accuracy.SECOND
          );
          const unixTimestamp = getCurrentUnixTimestamp({
            offsetSeconds: timestampOffsetSeconds,
          });
          dispatch({
            type: MERGE_OPERATION_DATA,
            payload: {
              selectPath: ['shop', shopId],
              data: {
                blacklistPaymentSources: {
                  [`${sourceType}_${source.id}`]: {
                    unixTimestamp,
                  },
                },
              },
            },
          });
        }

        // Clear shop operation data before redirect to external payment
        // This prevents duplicate transactions when users return from the payment site
        // and ensures clean state for re-selection
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['shop', shopId],
            data: {
              submitOrderId: undefined,
              selectPaymentSourceJointId: undefined,
              selectedDiamondPackId: undefined,
            },
          },
        });
        dispatch({ type: PERSIST_NOW });
        if (window.__isOnMdm) {
          location.assign(convertUrlToSafariScheme(redirectUrlString));
          return;
        }

        location.assign(redirectUrlString);
      }
    } catch (error) {
      let errorObject = {};
      try {
        errorObject = JSON.parse(error.message);
        // eslint-disable-next-line no-empty
      } catch {}

      const isUnverifiedUser = errorObject.code === 'UNVERIFIED_USER';
      errorLog('complete order failed', {
        ...logContext,
        errorCode: errorObject.code,
        error: error?.message,
      });

      dispatch(
        handleOrder({
          orderId,
          amount: getOrderData(state, orderId, 'amount'),
          status: orderStatusConstant.FAILED,
          currency: getOrderData(state, orderId, 'currency'),
          email: getOrderData(state, orderId, 'email'),
          items: getOrderData(state, orderId, 'items'),
          errorCode: errorObject.code,
        })
      );

      // Skip toastr for UNVERIFIED_USER - the order alert modal will handle the messaging
      if (!isUnverifiedUser) {
        dispatch(
          pushToastr({
            textKey: error?.message || 'something_went_wrong',
            color: 'error',
          })
        );
      }

      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath, error },
      });
    }
  };

export default submitCompleteOrder;
